/**
 * UnsubscribePage — public route at /unsubscribe.
 *
 * The footer of every campaign email points here with ?email=&sig=.
 * We submit those to POST /api/unsubscribe which HMAC-verifies the
 * signature server-side and flips the user's marketing flag off.
 *
 * No auth required. The page also offers a "Re-subscribe" link that
 * routes to /login (you have to be logged in to re-subscribe — prevents
 * a leaked link from re-enabling someone else).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { API } from "../../utils";

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const sig = params.get("sig") || "";
  const [state, setState] = useState({ loading: true, ok: false, err: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!email || !sig) {
        if (alive) setState({ loading: false, ok: false, err: "Missing email or signature in the link." });
        return;
      }
      try {
        await axios.post(`${API}/unsubscribe`, { email, sig });
        if (alive) setState({ loading: false, ok: true, err: "" });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Unsubscribe failed";
        if (alive) setState({ loading: false, ok: false, err: detail });
      }
    })();
    return () => { alive = false; };
  }, [email, sig]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-gray-900 border border-gray-800 rounded-2xl p-8" data-testid="unsubscribe-page">
        <div className="text-center mb-6">
          <div className="font-serif text-3xl font-black text-amber-400 mb-1">BLK PACKS</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500">Marketing Preferences</div>
        </div>

        {state.loading ? (
          <p className="text-gray-300 text-center" data-testid="unsubscribe-loading">Updating your preferences…</p>
        ) : state.ok ? (
          <div className="text-center" data-testid="unsubscribe-success">
            <div className="text-green-400 text-5xl mb-3">✓</div>
            <h2 className="text-white font-black text-xl mb-2">You're Unsubscribed</h2>
            <p className="text-gray-400 text-sm mb-1">No more marketing emails to <span className="text-white font-mono">{email}</span>.</p>
            <p className="text-gray-500 text-xs mb-6">You'll still receive account-essential emails (order confirmations, shipping, password resets).</p>
            <div className="flex flex-col gap-2">
              <Link to="/login" className="block w-full py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-bold text-sm" data-testid="unsubscribe-resub-link">
                Changed your mind? Log in to re-subscribe
              </Link>
              <Link to="/" className="block w-full py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm">
                Back to BLK PACKS
              </Link>
            </div>
          </div>
        ) : (
          <div className="text-center" data-testid="unsubscribe-error">
            <div className="text-red-400 text-5xl mb-3">×</div>
            <h2 className="text-white font-black text-xl mb-2">Unsubscribe Failed</h2>
            <p className="text-gray-400 text-sm mb-4">{state.err}</p>
            <p className="text-gray-500 text-xs mb-6">If you keep seeing this, log in and toggle marketing off from your profile settings.</p>
            <Link to="/login" className="block w-full py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-bold text-sm">
              Log in to manage preferences
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
